import { DotsThreeIcon, PlusIcon } from '@phosphor-icons/react';
import {
  Button,
  Card,
  Cell,
  Column,
  Menu,
  MultiSelect,
  Row,
  Select,
  type SelectOption,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
  TextField,
} from '@skupreme/shared/ui';
import type { ReactNode } from 'react';
import {
  describeRegions,
  FEE_UNITS,
  type MethodRow,
  type RegionTree,
  type RowErrors,
  type ShippingMethod,
} from '../../lib/shipping-template';

type ColumnId =
  | 'regions'
  | 'marketplaces'
  | 'days'
  | 'perOrder'
  | 'perItem'
  | 'unit'
  | 'actions';

/* Fixed widths, so the cards of every method line up under each other. */
const COLUMNS: { id: ColumnId; label: string; className?: string }[] = [
  { id: 'regions', label: 'Regions' },
  { id: 'marketplaces', label: 'Marketplaces', className: 'sk:w-52' },
  { id: 'days', label: 'Transit time (days)', className: 'sk:w-52' },
  { id: 'perOrder', label: 'Per order (USD)', className: 'sk:w-32' },
  { id: 'perItem', label: 'Per unit (USD)', className: 'sk:w-32' },
  { id: 'unit', label: 'Unit', className: 'sk:w-32' },
  { id: 'actions', label: '', className: 'sk:w-12' },
];

function Stack({ children }: { children: ReactNode }) {
  return <div className="sk:flex sk:flex-col sk:gap-0.5">{children}</div>;
}

export function CellError({ children }: { children?: ReactNode }) {
  return children ? (
    <span className="sk:text-xs sk:text-danger">{children}</span>
  ) : null;
}

type MethodCardProps = {
  method: ShippingMethod;
  tree: RegionTree;
  marketplaceOptions: SelectOption[];
  /** Shown only once a save has been tried. */
  error?: string;
  rowErrors: Record<string, RowErrors>;
  isDisabled: boolean;
  onAddRow: () => void;
  onEditRegions: (rowId: string) => void;
  onDeleteRow: (rowId: string) => void;
  onChangeRow: (rowId: string, patch: Partial<MethodRow>) => void;
  /** Absent for the methods every template keeps. */
  onRemove?: () => void;
};

/** One shipping speed: which regions it covers, how long and what it costs. */
export function MethodCard({
  method,
  tree,
  marketplaceOptions,
  error,
  rowErrors,
  isDisabled,
  onAddRow,
  onEditRegions,
  onDeleteRow,
  onChangeRow,
  onRemove,
}: MethodCardProps) {
  const renderCell = (column: ColumnId, row: MethodRow) => {
    const errors = rowErrors[row.id] ?? {};
    const change = (patch: Partial<MethodRow>) => onChangeRow(row.id, patch);

    switch (column) {
      case 'regions':
        return (
          <Stack>
            {describeRegions(row.regions, tree).map(line => (
              <span key={line} className="sk:line-clamp-2">
                {line}
              </span>
            ))}
            <CellError>{errors.regions}</CellError>
          </Stack>
        );
      case 'marketplaces':
        return (
          <MultiSelect
            aria-label="Marketplaces"
            options={marketplaceOptions}
            values={row.marketplaces}
            onChange={marketplaces => change({ marketplaces })}
            placeholder="Select marketplaces"
            isDisabled={isDisabled}
            className="sk:w-44"
          />
        );
      case 'days':
        return (
          <Stack>
            <span className="sk:inline-flex sk:items-center sk:gap-1.5">
              <TextField
                label="Min days"
                type="number"
                placeholder="Min"
                value={row.minDays}
                onChange={minDays => change({ minDays })}
                isDisabled={isDisabled}
                className="sk:w-20"
              />
              <span className="sk:text-fg-tertiary">–</span>
              <TextField
                label="Max days"
                type="number"
                placeholder="Max"
                value={row.maxDays}
                onChange={maxDays => change({ maxDays })}
                isDisabled={isDisabled}
                className="sk:w-20"
              />
            </span>
            <CellError>{errors.days}</CellError>
          </Stack>
        );
      case 'perOrder':
        return row.free ? (
          <span className="sk:font-medium">Free</span>
        ) : (
          <Stack>
            <TextField
              label="Fee per order"
              type="number"
              placeholder="0.00"
              value={row.perOrder}
              onChange={perOrder => change({ perOrder })}
              isDisabled={isDisabled}
              className="sk:w-20"
            />
            <CellError>{errors.perOrder}</CellError>
          </Stack>
        );
      case 'perItem':
        return row.free ? null : (
          <Stack>
            <TextField
              label="Fee per unit"
              type="number"
              placeholder="0.00"
              value={row.perItem}
              onChange={perItem => change({ perItem })}
              isDisabled={isDisabled}
              className="sk:w-20"
            />
            <CellError>{errors.perItem}</CellError>
          </Stack>
        );
      case 'unit':
        return row.free ? null : (
          <Stack>
            <Select
              aria-label="Fee unit"
              options={FEE_UNITS}
              value={row.unit}
              onChange={unit => change({ unit })}
              placeholder="Unit"
              isDisabled={isDisabled}
              isInvalid={!!errors.unit}
              className="sk:w-24"
            />
            <CellError>{errors.unit}</CellError>
          </Stack>
        );
      case 'actions':
        return (
          <Menu
            aria-label="Region row actions"
            trigger={
              <Button
                variant="ghost"
                shape="circle"
                aria-label="Region row actions"
                isDisabled={isDisabled}
                className="sk:size-6"
              >
                <DotsThreeIcon size={16} weight="bold" />
              </Button>
            }
            actions={[
              { id: 'edit', label: 'Edit regions' },
              { id: 'delete', label: 'Delete', isDanger: true },
            ]}
            onAction={action =>
              action === 'edit' ? onEditRegions(row.id) : onDeleteRow(row.id)
            }
          />
        );
    }
  };

  return (
    <Card
      title={method.name}
      action={
        <span className="sk:inline-flex sk:items-center sk:gap-2">
          {onRemove ? (
            <Button
              size="sm"
              variant="ghost"
              isDisabled={isDisabled}
              onPress={onRemove}
            >
              Remove
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="pill"
            isDisabled={isDisabled}
            onPress={onAddRow}
          >
            <PlusIcon size={14} aria-hidden />
            Add regions
          </Button>
        </span>
      }
      bodyClassName="sk:p-0"
    >
      <TableContainer>
        <Table aria-label={`${method.name} regions`} className="sk:table-fixed">
          <TableHeader>
            {COLUMNS.map(column => (
              <Column
                key={column.id}
                id={column.id}
                isRowHeader={column.id === 'regions'}
                aria-label={column.label ? undefined : 'Actions'}
                className={column.className}
              >
                {column.label}
              </Column>
            ))}
          </TableHeader>
          <TableBody
            renderEmptyState={() => (
              <div className="sk:flex sk:flex-col sk:items-center sk:gap-1 sk:px-3 sk:py-8 sk:text-fg-tertiary">
                No regions yet
                <CellError>{error}</CellError>
              </div>
            )}
          >
            {method.rows.map(row => (
              <Row key={row.id} id={row.id}>
                {COLUMNS.map(column => (
                  <Cell key={column.id}>{renderCell(column.id, row)}</Cell>
                ))}
              </Row>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
